import { useEffect, useRef } from "react";

/**
 * Parallax transform driven purely by window.scrollY.
 *
 * The parent's document offset/height is measured once (and on resize) instead of
 * calling getBoundingClientRect() every frame — reading layout inside the same
 * frame we write `transform` is what caused the forced-reflow warning.
 */
export const useParallax = (speed = -0.3) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let ticking = false;
        let parentTop = 0;
        let parentHeight = 0;

        const measure = () => {
            const element = ref.current;
            const parent = element?.parentElement;
            if (!parent) return;
            const rect = parent.getBoundingClientRect();
            parentTop = rect.top + window.scrollY;
            parentHeight = rect.height;
        };

        const update = () => {
            ticking = false;
            const element = ref.current;
            if (!element) return;

            // relative top of the parent within the viewport, computed without layout reads
            const top = parentTop - window.scrollY;
            if (top < window.innerHeight && top + parentHeight > 0) {
                element.style.transform = `translate3d(0, ${top * speed}px, 0)`;
            }
        };

        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
        };

        const handleResize = () => {
            measure();
            handleScroll();
        };

        measure();
        update(); // initial position

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleResize, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        };
    }, [speed]);

    return { ref };
};
